import useIsMounted from '@github-ui/use-is-mounted'
import {useEffect, useRef} from 'react'
import {useLocation} from '@github-ui/react-router'

/**
 * Hook that executes a handler function when the route changes.
 * The handler is not called on initial mount, but will be called on all subsequent
 * route changes, including navigating to the same pathname (useful for data refresh).
 *
 * @param handler - Callback to execute on route change. Should be memoized with useCallback to avoid unnecessary re-renders.
 *
 * @example
 * ```tsx
 * const handleRouteChange = useCallback(() => {
 *   refetchData()
 * }, [refetchData])
 *
 * useRespondToRouteChange(handleRouteChange)
 * ```
 */
export function useRespondToRouteChange(handler: () => void): void {
  const isMounted = useIsMounted()
  const location = useLocation()
  const isInitialMount = useRef(true)

  useEffect(() => {
    // Skip initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Call handler on all route changes (including same pathname)
    if (isMounted()) {
      handler()
    }
  }, [location, handler, isMounted])
}
